import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { buildApiUrl } from '../utils/serverOrigin';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MeetingAgent {
  id: string;
  label: string;
  emoji: string;
}

export interface Opinion {
  round: number;
  agentId: string;
  label: string;
  emoji: string;
  text: string;
  isDropout?: boolean;
  timestamp?: string;
}

export interface HumanInput {
  speaker: string;
  message: string;
  timestamp?: string;
  beforeRound?: number;
}

export interface MeetingState {
  date: string;
  status: 'waiting' | 'in_progress' | 'completed';
  currentRound: number;
  maxRounds: number;
  activeAgents: string[];
  droppedAgents: string[];
  agents: MeetingAgent[];
  opinions: Opinion[];
  humanInputs: HumanInput[];
  updatedAt: string;
}

const EMPTY_STATE: MeetingState = {
  date: '',
  status: 'waiting',
  currentRound: 0,
  maxRounds: 5,
  activeAgents: [],
  droppedAgents: [],
  agents: [],
  opinions: [],
  humanInputs: [],
  updatedAt: '',
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useMeetingState() {
  const [meetingState, setMeetingState] = useState<MeetingState>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastUpdatedRef = useRef<string>('');

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch('/meeting-state.json?t=' + Date.now());
      if (!res.ok) {
        if (res.status === 404) {
          // No meeting state file yet — that's fine
          setMeetingState(EMPTY_STATE);
          setError(null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json() as MeetingState;

      // Only update if actually changed
      if (data.updatedAt !== lastUpdatedRef.current) {
        lastUpdatedRef.current = data.updatedAt;
        setMeetingState(data);
      }
      setError(null);
    } catch (err) {
      // Don't wipe existing state on transient errors
      if (meetingState.date === '') {
        setError((err as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [meetingState.date]);

  // Initial fetch and polling
  useEffect(() => {
    void fetchState();

    // Poll every 3 seconds
    pollRef.current = setInterval(() => {
      void fetchState();
    }, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchState]);

  // Send human input
  const sendHumanInput = useCallback(async (speaker: string, message: string) => {
    try {
      const gatewayToken = useAppStore.getState().gatewayToken;
      await fetch(buildApiUrl('/api/team/meeting-input', useAppStore.getState().gatewayUrl), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${gatewayToken}`,
        },
        body: JSON.stringify({
          speaker,
          message,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch {
      // Fallback: write directly via the static file approach
      // The script watches this file
      console.warn('[useMeetingState] API fallback — writing human input directly');
    }
  }, []);

  return { meetingState, loading, error, sendHumanInput, refetch: fetchState };
}
